import React, { useState } from 'react'
import FloatingMenu from 'components/menu/floating'
import { MenuHeaderAction, MenuItem, MenuList } from 'components/menu/items'
import ColourField from 'components/colour-field'
import HeaderPreview from 'components/header-preview'
import { Colour, Text } from 'components/icons'
import IconSelector from 'components/spreadsheet/components/cell/components/icon'
import { IContextMenuState, ITableViewColumn } from 'types'

export interface HeaderColours {
  headerBackgroundColor: string
  headerTextColor: string
}

interface HeaderFormattingMenuProps {
  id: string
  menuState: IContextMenuState
  setMenuState: (menuState: IContextMenuState) => void
  column: ITableViewColumn
  // The colours a header starts with in this hub, which Reset puts back.
  defaults: HeaderColours
  // Saves a colour as it's picked.
  onChange: (patch: Partial<HeaderColours>) => void
}

const PANEL_WIDTH = 280

const sameColour = (a: string, b: string): boolean => a.toLowerCase() === b.toLowerCase()

// The whole row opens its picker, not only the swatch: a click elsewhere on the row clicks the swatch. One on the
// swatch never reaches the row, as a MenuItem's end slot keeps its clicks to itself.
const openRowPicker = (event: React.SyntheticEvent<HTMLElement>): void => {
  event.currentTarget.querySelector<HTMLElement>('[aria-haspopup="dialog"]')?.click()
}

/**
 * A column header's colours, in a panel of their own from the column's menu: the header as it will look,
 * following a colour while it's picked, and a swatch for each colour, saved as it's picked. Reset puts back
 * the colours the hub gives new columns.
 */
const HeaderFormattingMenu: React.FC<HeaderFormattingMenuProps> = ({
  id,
  menuState,
  setMenuState,
  column,
  defaults,
  onChange
}) => {
  const [backgroundDraft, setBackgroundDraft] = useState<string | null>(null)
  const [textDraft, setTextDraft] = useState<string | null>(null)
  // The row whose picker is open, marked as open, so pressing it again closes the picker (see Menu).
  const [openRow, setOpenRow] = useState<'background' | 'text' | null>(null)
  const rowOpenChange = (row: 'background' | 'text') => (open: boolean) =>
    setOpenRow((current) => (open ? row : current === row ? null : current))
  // An unset colour can be null or empty, and either way the header takes the hub's, as the table draws it.
  const background = column.headerBackgroundColor || defaults.headerBackgroundColor
  const text = column.headerTextColor || defaults.headerTextColor
  const isDefault = sameColour(background, defaults.headerBackgroundColor) && sameColour(text, defaults.headerTextColor)

  return (
    <FloatingMenu
      id={id}
      menuState={menuState}
      setMenuState={setMenuState}
      width={PANEL_WIDTH}
      zIndex={3000}
      title="Header formatting"
      action={!isDefault && <MenuHeaderAction onClick={() => onChange(defaults)}>Reset</MenuHeaderAction>}
    >
      {/* The header as it will look, with the column's own icon and name. */}
      <div className="flex justify-center px-3 pt-3 pb-1">
        <HeaderPreview
          background={backgroundDraft ?? background}
          text={textDraft ?? text}
          label={
            <>
              <span className="flex shrink-0 [&_svg]:size-3.5">
                <IconSelector kind={column.kind} />
              </span>
              <span className="truncate">{column.name}</span>
            </>
          }
        />
      </div>
      <MenuList>
        <MenuItem
          icon={<Colour />}
          end={
            <ColourField
              label="Background"
              value={background}
              variant="swatch"
              nested
              confirm={false}
              onDraft={setBackgroundDraft}
              onOpenChange={rowOpenChange('background')}
              onSave={(colour) => {
                onChange({ headerBackgroundColor: colour })
                return Promise.resolve(true)
              }}
            />
          }
          aria-expanded={openRow === 'background'}
          data-tour="column-header-background"
          onClick={openRowPicker}
        >
          Background
        </MenuItem>
        <MenuItem
          icon={<Text />}
          end={
            <ColourField
              label="Text"
              value={text}
              variant="swatch"
              nested
              confirm={false}
              onDraft={setTextDraft}
              onOpenChange={rowOpenChange('text')}
              onSave={(colour) => {
                onChange({ headerTextColor: colour })
                return Promise.resolve(true)
              }}
            />
          }
          aria-expanded={openRow === 'text'}
          onClick={openRowPicker}
        >
          Text
        </MenuItem>
      </MenuList>
    </FloatingMenu>
  )
}

export default HeaderFormattingMenu
